import { useCallback, useEffect, useRef, useState } from "react";

interface UseDropdownOptions {
	open?: boolean;
	setOpen?: (open: boolean) => void;
	returnFocusOnClose?: boolean;
}

const useDropdown = ({
	open: controlledOpen,
	setOpen: controlledSetOpen,
	returnFocusOnClose = true,
}: UseDropdownOptions = {}) => {
	const isControlled = controlledOpen !== undefined;

	const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
	const open = isControlled ? controlledOpen : uncontrolledOpen;
	const setOpen = isControlled ? controlledSetOpen! : setUncontrolledOpen;

	const menuRef = useRef<HTMLDivElement | null>(null);
	const buttonRef = useRef<HTMLButtonElement | null>(null);

	const handleClose = useCallback(
		(opts?: { returnFocus?: boolean }) => {
			setOpen(false);

			if ((opts?.returnFocus ?? returnFocusOnClose) && buttonRef.current) {
				buttonRef.current.focus();
			}
		},
		[setOpen, returnFocusOnClose]
	);

	const handleToggle = useCallback(() => {
		setOpen(!open);
	}, [open, setOpen]);

	const closeOnOutsideClick = useCallback(
		(e: MouseEvent) => {
			const target = e.target as HTMLElement;

			const clickedInsideMenu = menuRef.current?.contains(target);
			const clickedOwnButton = buttonRef.current?.contains(target);
			const clickedDropdownTrigger = target.closest("[data-dropdown-trigger='true']");

			if (clickedDropdownTrigger && !clickedOwnButton) {
				handleClose();
				return;
			}

			if (!clickedInsideMenu && !clickedOwnButton) {
				handleClose();
			}
		},
		[handleClose]
	);

	useEffect(() => {
		if (!open) return;

		const onKeyDown = (e: KeyboardEvent) => {
			if (e.key === "Escape" || e.key === "Esc") {
				e.preventDefault();
				e.stopPropagation();
				handleClose({ returnFocus: true });
			}
		};

		document.addEventListener("keydown", onKeyDown);
		document.addEventListener("mousedown", closeOnOutsideClick);

		return () => {
			document.removeEventListener("keydown", onKeyDown);
			document.removeEventListener("mousedown", closeOnOutsideClick);
		};
	}, [open, handleClose, closeOnOutsideClick]);

	return {
		open,
		menuRef,
		buttonRef,
		handleToggle,
		handleClose,
	};
};

export default useDropdown;
